// Wire shapes + the api surface. `kit.ts` serves it on the kit core (@hq-org/kit-wasm: the session's local store, its
// declared queries and asks; net.ts for the plain HTTP), `mock/` from fixtures for two instances (michael + a
// connected digiterium); both implement `Api`. Every response is parsed through these schemas at the boundary.
// Records from another instance carry `instance` and an address id (`<instance>/<id>`, Concept federation-via-home):
// the home merges them (nothing copied); the UI treats ids as opaque and shows `local(id)`.
import { z } from "zod";

/** The instance part of an address id (`digiterium/tsk_x` → digiterium); null = the home's own record. */
export const instanceOf = (id: string): string | null => (id.includes("/") ? id.slice(0, id.indexOf("/")) : null);
/** The id without its instance (what cards and heads show). */
export const local = (id: string) => id.slice(id.lastIndexOf("/") + 1);
/** A namespaced kind's short name (`hq.tasks.Task` → Task, `truss.Person` → Person). */
export const shortKind = (k: string) => k.slice(k.lastIndexOf(".") + 1);

// The board and chat are the client cores' views; their shapes are generated from the cores' contracts
// (platform:Interface/board, chat:Interface/views; rigid Decision contracts-from-fields), not written here.
import type {
	BoardQuery,
	BoardView,
	Column as BoardColumnView,
	ColumnEdit,
	Project,
	Repo,
	Snapshot,
	Status,
	Task,
} from "@hq-org/contracts/board";
import type { Conversation, Message, Overview, Reaction, Space, Thread } from "@hq-org/contracts/chat";
export type { BoardColumnView, BoardQuery, BoardView, ColumnEdit, Conversation, Message, Overview, Project, Reaction, Repo, Snapshot, Space, Status, Task, Thread };
/** The Chats page (the core's `chat.overview`). */
export type ChatOverview = Overview;

/** A status's name (a board column): data, the hq.tasks.Status records a space sees; never a list written here. */
export type TaskStatus = string;

export const KINDS = ["Task", "Project", "Person", "Repo"] as const;
export const Kind = z.enum(KINDS);
export type Kind = z.infer<typeof Kind>;

export const Person = z.object({ id: z.string(), name: z.string(), kind: z.enum(["human", "agent"]).catch("human") });
export type Person = z.infer<typeof Person>;


export const Link = z.object({
	url: z.string(),
	kind: z.enum(["commit", "pr", "issue", "tg", "blob", "url"]),
	title: z.string().nullable(),
	addedAt: z.string(),
});
export type Link = z.infer<typeof Link>;

export const Event = z.object({
	at: z.string(),
	by: z.string(),
	field: z.string(),
	from: z.unknown(),
	to: z.unknown(),
	note: z.string().nullable(),
});
export type Event = z.infer<typeof Event>;

/** One field row in the side panel; `ref` = id of a related entity (clickable), `kind` its kind when known. */
export const Field = z.object({ key: z.string(), value: z.string(), ref: z.string().optional(), kind: z.string().optional() });
export type Field = z.infer<typeof Field>;

/** A record a task is about (Task.context, Decision task-context): any kind; note = why it is here. */
export const ContextItem = z.object({ id: z.string(), kind: z.string(), label: z.string(), note: z.string().nullable() });
export type ContextItem = z.infer<typeof ContextItem>;

/** Attach / detach context records (ids of any kind). */
export type ContextChange = { add?: string[]; remove?: string[]; note?: string };

/** Side-panel view of any entity. */
export const Detail = z.object({
	id: z.string(),
	/** Short kind (Task, Project, Person, Repo, Space, Thread, Message, Member, …); `fullKind` the namespaced one. */
	kind: z.string(),
	fullKind: z.string().nullable().default(null),
	instance: z.string().nullable().default(null),
	/** Records pointing at this one (the other end's field name as key): the graph page's links both ways. */
	backlinks: z.array(Field).default([]),
	name: z.string(),
	status: z.string().nullable(),
	body: z.string().nullable(),
	/** `body` is the kind's markdown field: shown rendered. */
	markdown: z.boolean().optional(),
	/** The markdown body as rigid-core's document tree (the client core parses it; no HTML). */
	doc: z.custom<MdDoc>().optional(),
	fields: z.array(Field),
	links: z.array(Link),
	events: z.array(Event),
	/** Tasks only: the records it is about, oldest first. */
	context: z.array(ContextItem).default([]),
});
export type Detail = z.infer<typeof Detail>;

/** rigid-core's markdown document (Decision markdown-in-rigid-core): generated from rigid:Interface/markdown. */
import type { Block as MdBlock, Doc as MdDoc, Inline as MdInline } from "@hq-org/contracts/markdown";
export type { MdBlock, MdDoc, MdInline };

export class ApiError extends Error {
	constructor(
		message: string,
		readonly status = 0,
	) {
		super(message);
		this.name = "ApiError";
	}
	get unauthorized() {
		return this.status === 401;
	}
}

/** The signed-in session (Plan admin-usage: Query.session); permissions = the global list, '*' expanded; projects =
 *  per project the caller is a member of, what its Member roles grant there (Decision project-membership). */
export const Session = z.object({
	person: Person,
	/** The home instance this session is on. */
	instance: z.string().default(""),
	tokenName: z.string(),
	permissions: z.array(z.string()),
	projects: z.array(z.object({ project: z.object({ id: z.string() }), permissions: z.array(z.string()) })).default([]),
});
export type Session = z.infer<typeof Session>;

// ---------- platform pages ----------

/** An instance in the merged view: the home and every one it has a Connect grant into. */
export const Instance = z.object({ name: z.string(), url: z.string(), home: z.boolean(), live: z.boolean().default(true) });
export type Instance = z.infer<typeof Instance>;


export const KindField = z.object({ name: z.string(), type: z.string(), required: z.boolean().default(false), many: z.boolean().default(false) });
export type KindField = z.infer<typeof KindField>;
/** One kind of an installed package (meta Kind with its Fields and Relations). */
export const KindInfo = z.object({
	name: z.string(),
	description: z.string().nullable().default(null),
	fields: z.array(KindField),
	relations: z.array(z.object({ name: z.string(), to: z.string(), many: z.boolean().default(false), inverse: z.string().nullable().default(null) })).default([]),
	count: z.number().nullable().default(null),
});
export type KindInfo = z.infer<typeof KindInfo>;

/** An ontology package (ontology + hooks + handlers + defaults), installed or offered. */
export const Package = z.object({
	name: z.string(),
	version: z.string(),
	description: z.string().nullable().default(null),
	installed: z.string().nullable(),
	installedAt: z.string().nullable().default(null),
	kinds: z.array(KindInfo).default([]),
	/** Behaviour (hooks/handlers): installing it needs the owner's consent. */
	hooks: z.array(z.string()).default([]),
	base: z.boolean().default(false),
});
export type Package = z.infer<typeof Package>;

/** Install/upgrade preview: what the package adds; `refused` lists the non-add-only changes (an upgrade with any is refused). */
export const PackageDiff = z.object({
	name: z.string(),
	from: z.string().nullable(),
	to: z.string(),
	added: z.array(z.object({ what: z.enum(["kind", "field", "relation", "rule", "hook"]), name: z.string(), detail: z.string().nullable().default(null) })),
	refused: z.array(z.object({ name: z.string(), reason: z.string() })).default([]),
	hooks: z.array(z.string()).default([]),
});
export type PackageDiff = z.infer<typeof PackageDiff>;

/** An app session from auth: the bearer, its refresh token, seconds it lives. */
export type Login = { access: string; refresh: string; expiresIn: number };

/** The operator re-reads the release channel on every pass (10 min) but writes observed only when something changed,
 *  and stamps its heartbeat observed.seenAt hourly (hq.infra 1.9.0; failing or not, in the write usage makes then).
 *  An observation whose seenAt is older than this is from an operator that is not running (platform's, 22 h after the
 *  rows-written limit of 2026-10-26): its `available` may be stale, so no Update is offered from it. One without
 *  seenAt (an operator before 1.9.0, or none observing it) is taken as it is. */
export const OPERATOR_SILENT_MS = 3 * 3600_000;
/** When the operator observing this Deployment was last heard of, if that is too long ago; else null. */
export function operatorSilentSince(observed: { seenAt?: unknown } | null | undefined, now = Date.now()): string | null {
	const at = typeof observed?.seenAt === "string" ? Date.parse(observed.seenAt) : Number.NaN;
	return Number.isFinite(at) && now - at > OPERATOR_SILENT_MS ? (observed!.seenAt as string) : null;
}
/** hq.files (hq Decision files-model): a folder, a file with its newest version, one version (its bytes at `blob`,
 *  <space>/<sha256>, read by the space's members). */
export type FileFolder = { id: string; name: string; space: string; parent: string | null };
export type FileVersion = { id: string; file: string; n: number; sha256: string; blob: string; size: number; contentType: string; at: string | null };
/** A file sent with a chat message: the attached version and its file's name. */
export type Attachment = FileVersion & { name: string };
export type FileItem = { id: string; name: string; space: string; folder: string | null; latest: FileVersion | null };
/** A version's bytes as the viewers take them: an object URL typed by its contentType (the caller revokes it), the
 *  text of a text kind, and markdown's document tree. */
export type FileContent = { url: string; text: string | null; doc: MdDoc | null };

/** A managed instance: its Deployment (`id`), the build it should run (`build`) and the one it runs (`running`; they
 *  differ while the operator rolls out), health, region, domain, the Hosting account, the operator's meters. */
/** Where a proposal was read (hq.knowledge.Source): a file's line span or page, or a chat message. */
export type ReviewSource = { id: string; type: string; title: string | null; path: string | null; lines: { from: number; to: number } | null; page: number | null; date: string | null; quote: string | null; file: string | null };
/** A source with the records made from it: kind, label and status from the proposal that wrote each (else its id). */
export type SourceUse = ReviewSource & { records: { id: string; kind: string; label: string; status: string | null }[] };
/** One change a space's housekeeper proposes (hq.knowledge.Proposal, hq Decision housekeeper). */
export type ReviewItem = { id: string; key: string; status: string; kind: string; op: string; summary: string; why: string | null; error: string | null; fields: [string, string][]; sources: ReviewSource[]; questions: ReviewQuestion[]; answers: Record<string, string> };
/** What a waiting proposal asks its owner: a value for `field` (a patch path) the sources do not give, or it waits for another proposal. */
export type ReviewQuestion = { field: string | null; waitsFor: string | null; text: string };

export type Managed = {
	id: string;
	instance: string;
	instanceId: string | null;
	url: string | null;
	domain: string | null;
	region: string | null;
	status: string;
	build: string | null;
	running: string | null;
	ready: boolean;
	error: string | null;
	hosting: string | null;
	account: string | null;
	usage: Meter[];
	/** the newest signed release when it is not `build` (hq Decision self-managing-instances): Update sets build to it */
	available: string | null;
	/** the operator takes a new release without the owner's Update */
	auto: boolean;
	/** the operator holds this one's rollout (observed.held: "waiting for <instance>" | "<instance> failed: <build>
	 *  rolled back"): Update all rolls one instance at a time and holds the rest when one fails */
	held: string | null;
};

/** One meter of an instance's Deployment (observed.usage, hq Decision cloudflare-oauth); `limit` null = none known
 *  (one Durable Object's share). */
export const Meter = z.object({
	service: z.string(),
	metric: z.string(),
	used: z.number().nullable(),
	limit: z.number().nullable(),
	unit: z.string(),
	period: z.enum(["day", "month", "total"]),
	scope: z.string().nullable(),
	resetsAt: z.string().nullable(),
	/** its share of the limit, as the operator wrote it (none without a limit) */
	pct: z.number().nullable().catch(null),
	source: z.string(),
	error: z.string().nullable(),
});
export type Meter = z.infer<typeof Meter>;
/** A Deployment's meters as the operator last wrote them (`at`). */
export const Usage = z.object({ at: z.string(), meters: z.array(Meter) });
export type Usage = z.infer<typeof Usage>;

/** A truss.Secret as the page shows it: metadata only (reads = its `read` events). */
export const SecretRow = z.object({
	id: z.string(),
	profile: z.string(),
	key: z.string(),
	status: z.enum(["active", "removed"]),
	rev: z.number().nullable(),
	changedAt: z.string().nullable(),
	recipients: z.array(z.string()),
	reads: z.number().nullable(),
	lastReadAt: z.string().nullable(),
	lastReadBy: z.object({ name: z.string() }).nullable(),
});
export type SecretRow = z.infer<typeof SecretRow>;
/** A public age key the project's secrets are encrypted to, and who holds it ("?" = nobody known here); `device`: a
 *  truss.DeviceKey of mine (revocable here). */
export type Recipient = { key: string; names: string[]; device?: string };
export type SecretsData = {
	secrets: SecretRow[];
	recipients: Recipient[];
	/** a connected instance's project: metadata only */
	remote?: boolean;
};

/** A sign-in of mine (auth `/sessions`): this app in a browser, a browser session on /auth, the CLI. */
export const SignIn = z.object({
	id: z.string(),
	kind: z.string(),
	agent: z.string().nullable().default(null),
	createdAt: z.string(),
	usedAt: z.string().nullable().default(null),
	current: z.boolean().default(false),
});
export type SignIn = z.infer<typeof SignIn>;

/** An app I authorized over OAuth (auth `/grants` that are not another instance's home): Claude, ChatGPT, … */
export const AppGrant = z.object({ id: z.string(), name: z.string(), scopes: z.array(z.string()).default([]), createdAt: z.string() });
export type AppGrant = z.infer<typeof AppGrant>;

/** A Connect grant: outgoing = this home's key into another instance; incoming = another home's key into this one. */
export const Connection = z.object({
	id: z.string(),
	direction: z.enum(["outgoing", "incoming"]),
	instance: z.string(),
	url: z.string(),
	/** Who the grant acts as on the granting side. */
	person: z.string().nullable().default(null),
	scopes: z.array(z.string()).default([]),
	createdAt: z.string(),
	lastUsedAt: z.string().nullable().default(null),
	status: z.enum(["active", "revoked", "pending"]),
});
export type Connection = z.infer<typeof Connection>;

export const Role = z.object({ id: z.string(), name: z.string(), permissions: z.array(z.string()) });
export type Role = z.infer<typeof Role>;
/** truss.Member: a person's roles in a space (or the instance when `space` is null). */
export const Member = z.object({
	id: z.string(),
	person: Person,
	space: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
	roles: z.array(z.string()),
	status: z.enum(["active", "removed"]).default("active"),
});
export type Member = z.infer<typeof Member>;

/** A personal access token (opaque, revoked instantly); the secret itself is shown once at creation. */
export const Token = z.object({
	id: z.string(),
	name: z.string(),
	createdAt: z.string(),
	lastUsedAt: z.string().nullable().default(null),
	expiresAt: z.string().nullable().default(null),
	revoked: z.boolean().default(false),
});
export type Token = z.infer<typeof Token>;

/** The record graph: any kind, edges = relations (the graph page); an edge's kind is its relation's, as the package
 *  declares it (KindInfo hierarchy/dependency; the rest associations). */
export const EdgeKind = z.enum(["hierarchy", "dependency", "association"]);
export type EdgeKind = z.infer<typeof EdgeKind>;
export const GraphData = z.object({
	nodes: z.array(
		z.object({
			id: z.string(),
			kind: z.string(),
			name: z.string(),
			status: z.string().nullable().default(null),
			/** its own colour (a status) or its state's (a task) */
			color: z.string().nullable().default(null),
			instance: z.string().nullable().default(null),
		}),
	),
	edges: z.array(z.object({ from: z.string(), to: z.string(), type: z.string(), edgeKind: EdgeKind })),
});
export type GraphData = z.infer<typeof GraphData>;

export type Api = {
	session: {
		/** Person behind a token; rejects with ApiError(401) for a bad one. */
		me(token: string): Promise<Person>;
		/** Token name and permissions; absent until the server has Query.session (Plan admin-usage). */
		get?(): Promise<Session>;
	};
	snapshot: {
		load(): Promise<Snapshot>;
	};
	board: {
		/** The board for a filter, now and on every change. */
		view(q: BoardQuery, cb: (v: BoardView) => void): { update(q: BoardQuery): void; close(): void };
		/** Status only (buttons, keyboard): the card keeps its rank. Shows at once, rolls back if refused. */
		move(id: string, status: TaskStatus): Promise<void>;
		/** A drop into `status` right after card `after` (null = the top): status + rank. */
		place(id: string, status: TaskStatus, after: string | null): Promise<void>;
		/** The statuses `space` sees and hides (own or inherited), now and on every change. */
		columns(space: string, cb: (v: Status[] | null, error: string | null) => void): () => void;
		/** Add, rename, recolour, move or hide a column (board.column). */
		column(edit: ColumnEdit): Promise<void>;
	};
	tasks: {
		/** Attach or detach context records (Decision task-context). */
		context(id: string, change: ContextChange): Promise<void>;
		/** Ids of tasks whose title or body matches `q` (full-text, whole words). */
		search(q: string): Promise<string[]>;
	};
	entities: {
		get(id: string): Promise<Detail>;
	};
	/** The instances in the merged view (home first). */
	instances: {
		list(): Promise<Instance[]>;
	};
	graph: {
		/** Every record the session may see, any kind, with its relations. */
		load(): Promise<GraphData>;
	};
	chat: {
		spaces(): Promise<Space[]>;
		/** A message's body as a markdown document (the client core parses; components/markdown.tsx draws it); null:
		 *  the core could not. */
		markdown(text: string): Promise<MdDoc | null>;
		/** The Chats page, now and on every change: every space with its threads (general first, then the newest), unread
		 *  counts (the core's `chat.overview`, watched over the local replica). */
		overview(cb: (o: ChatOverview | null, error: string | null) => void): () => void;
		/** The Chats list, now and on every change: every open thread, newest message first, with its space's name; `space`
		 *  = that space and the spaces nested under it (the core's `chat.inbox`, watched over the local replica). */
		inbox(space: string | null, cb: (threads: Thread[] | null, error: string | null) => void): () => void;
		/** Threads of a space, or about one record (`about`), newest first. */
		threads(q: { space?: string; about?: string }): Promise<Thread[]>;
		messages(thread: string): Promise<Message[]>;
		/** A thread panel, now and on every change (this device's unsent messages included, `pending`): the thread (given,
		 *  or the one about a record; null until its first message) and its messages. */
		conversation(to: { thread: string } | { about: string }, cb: (c: { thread: string | null; messages: Message[] }, error: string | null) => void): () => void;
		/** Posts to a thread; `about` (+ space) opens the record's thread first when it has none yet. */
		send(to: { thread: string } | { about: string; space?: string | null; title?: string }, body: string): Promise<Message>;
		react(message: string, emoji: string): Promise<void>;
		/** Private read mark (hq.chat.ReadMark). */
		read(thread: string, message: string): Promise<void>;
		/** Files sent with a message (hq.files 1.1.0): each a File of the message's space with one Version attached to it. */
		attach(message: string, files: File[]): Promise<void>;
		/** A thread's attachments, by message id. */
		attachments(thread: string): Promise<Record<string, Attachment[]>>;
	};
	ontologies: {
		/** Installed packages, then the ones the registry offers (installed = null). `instance`: a connected one's
		 *  installed packages only (read through the home's grant). */
		list(instance?: string): Promise<Package[]>;
		/** `pkg` = a package given as JSON (Install from JSON: a private package, never in the registry); else the registry's. */
		preview(name: string, version: string, pkg?: Record<string, unknown>): Promise<PackageDiff>;
		/** Install or upgrade; `consent` = the owner accepts the package's hooks. */
		install(name: string, version: string, consent: boolean, pkg?: Record<string, unknown>): Promise<void>;
	};
	/** Where I'm signed in and which apps hold my grants; each can be ended. */
	access: {
		sessions(): Promise<SignIn[]>;
		endSession(id: string): Promise<void>;
		/** Every session but this one. */
		endOthers(): Promise<void>;
		apps(): Promise<AppGrant[]>;
		revokeApp(id: string): Promise<void>;
	};
	connections: {
		list(): Promise<Connection[]>;
		/** Starts Connect (OAuth) to another instance: the URL of its consent page. */
		connect(url: string): Promise<{ redirect: string | null }>;
		revoke(id: string): Promise<void>;
	};
	members: {
		/** An invite link (auth:Decision/invite-links) into `space` with `roles`: single use, 7 days. Someone with their
		 *  own instance opens it from there and Connects (federation); someone without gets a Person and a passkey. */
		invite(space: string, roles: string[]): Promise<string>;
		list(): Promise<Member[]>;
		roles(): Promise<Role[]>;
		add(person: { id?: string; name?: string }, roles: string[], space?: string | null): Promise<void>;
		remove(id: string): Promise<void>;
	};
	tokens: {
		list(): Promise<Token[]>;
		create(name: string): Promise<{ token: Token; secret: string }>;
		revoke(id: string): Promise<void>;
	};
	/** Sign-in (auth/contracts), before or around the bearer: explicit URLs, no app token. */
	auth: {
		/** `<base>/.well-known/truss`: the instance's name and its auth issuer (null = a truss without auth). */
		discover(base: string): Promise<{ instance: string | null; auth: string | null }>;
		/** A PAT \u2192 an app session (access JWT ~15 min + refresh token). */
		login(auth: string, pat: string): Promise<Login>;
		refresh(auth: string, refreshToken: string): Promise<Login>;
		logout(auth: string, refreshToken: string): Promise<void>;
		/** The issuer's browser session (a passkey sign-in on its pages) \u2192 an app session; null = none. */
		browser(auth: string): Promise<Login | null>;
	};
	/** Cloudflare's OAuth token endpoint (hq Decision cloudflare-oauth): a PKCE code for a grant, from the browser. */
	cloudflare: {
		token(form: Record<string, string>): Promise<{ access_token: string; refresh_token?: string; expires_in?: number; scope?: string }>;
	};
	/** The instances this home manages (Michael 1a 2a): every hq.infra Deployment it holds (the platform's Fleet),
	 *  with its Instance and Hosting; none when it holds none. Read from the synced records, no new endpoint. */
	/** A housekeeper's batch for review (hq Decision housekeeper): its proposals with their sources; approve or reject
	 *  them (the housekeeper writes the approved ones, with the id and tx fixed in the proposal). */
	knowledge: {
		batch(id: string): Promise<ReviewItem[]>;
		decide(ids: string[], status: "approved" | "rejected"): Promise<void>;
		/** A waiting proposal's answers ({field: value}); the housekeeper writes them on its next apply. */
		answer(id: string, answers: Record<string, string>): Promise<void>;
		/** The sources read at a record (a file version, a thread, a message, or the source itself), with what was made
		 *  from each. */
		sources(at: string): Promise<SourceUse[]>;
	};
	fleet: {
		list(): Promise<Managed[]>;
		/** the Deployment's spec: `build` (Update) or `auto`; the operator does the rest */
		update(id: string, patch: { build?: string; auto?: boolean }): Promise<void>;
	};
	/** Sealed secrets of a project (truss:Interface/secrets): metadata only; a value is encrypted in the browser to
	 *  the project's recipients and never comes back. A connected instance's project (`<instance>/<space>`): metadata
	 *  only (`remote`), its recipients and writes stay there. */
	secrets: {
		list(project: string): Promise<SecretsData>;
		/** Encrypts `value` to the current recipients and stores it (a changed recipient list is retried once). */
		save(project: string, profile: string, key: string, value: string): Promise<{ rev: number }>;
		remove(project: string, profile: string, key: string): Promise<void>;
		/** A GET secrets answer as JSON text (ciphertexts; every value in it is logged as read); `refs` empty = all. */
		read(project: string, refs?: string[]): Promise<string>;
		/** Re-seals the stale values: `plan` (the core) turns the answer, the recipients and the store's base into PUTs. */
		reseal(project: string, plan: (body: string, current: string[], endpoint: string) => string): Promise<number>;
		/** Registers this device's public key as a truss.DeviceKey of mine (hq:Decision/device-keys): its id. */
		addDevice(name: string, ageKey: string): Promise<string>;
		revokeDevice(id: string): Promise<void>;
	};
	/** Files in spaces (hq Decision files-model): folders, files, versions; the bytes through /blobs. */
	files: {
		/** What `folder` holds (null = the space's top): its folders and files, by name. */
		list(space: string, folder: string | null): Promise<{ folders: FileFolder[]; files: FileItem[] }>;
		/** A file's versions, newest first. */
		versions(file: string): Promise<FileVersion[]>;
		/** `blob` as the next version of `to.file`, or as a new file in `to.space` (`to.folder`): the file's id. */
		upload(to: { file: string } | { space: string; folder: string | null }, blob: File): Promise<string>;
		/** A new folder: its id. */
		folder(space: string, name: string, parent: string | null): Promise<string>;
		open(v: FileVersion): Promise<FileContent>;
	};
	blobs: {
		/** Object URL for a `blob://<key>` link (caller revokes it). */
		open(url: string): Promise<string>;
		/** Stores `blob` under <space>/<sha256> (hashed here; the instance checks it, and keeps one copy). */
		put(space: string, blob: Blob): Promise<{ key: string; sha256: string; size: number }>;
		/** The bytes at `key`. */
		get(key: string): Promise<Blob>;
		/** Mock only: a plain URL for a fixture image, usable in <img> at once. */
		direct?(url: string): string | null;
	};
};
